import { useDeferredValue, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeftRight, Plus } from 'lucide-react';
import { PERMISSIONS, StockTransferStatus, type StockTransferSummary } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { PageHeader } from '@/components/ui/PageHeader';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { useBranch } from '@/hooks/useBranch';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorMessage } from '@/services/apiClient';
import { useTransfersQuery } from '../api/transfers.api';
import { CreateTransferDrawer } from '../components/CreateTransferDrawer';
import { TransferDetailDrawer } from '../components/TransferDetailDrawer';
import { TransferStatusBadge } from '../components/TransferStatusBadge';

const PAGE_SIZE = 20;

/**
 * Transfers between the tenant's branches.
 *
 * The list is tenant-wide rather than branch-filtered: a manager needs to see what is heading to
 * a branch as much as what left it, and the row itself names both ends.
 */
export function TransfersPage() {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const { activeBranch } = useBranch();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StockTransferStatus | ''>('');
  const [page, setPage] = useState(1);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  const deferredSearch = useDeferredValue(search);

  const { data, isPending, isError, error, refetch, isFetching } = useTransfersQuery({
    page,
    pageSize: PAGE_SIZE,
    search: deferredSearch,
    status,
  });

  const canTransfer = can(PERMISSIONS.INVENTORY_TRANSFER);

  const statusOptions = [
    { value: '', label: t('inventory.transfers.filters.all') },
    ...Object.values(StockTransferStatus).map((value) => ({
      value,
      label: t(`inventory.transfers.status.${value}`),
    })),
  ];

  const columns: Column<StockTransferSummary>[] = [
    {
      key: 'reference',
      header: t('inventory.transfers.fields.reference'),
      primary: true,
      render: (transfer) => (
        <span className="flex flex-col">
          <span className="text-ink font-mono font-medium" dir="ltr">
            {transfer.reference}
          </span>
          <span className="text-muted text-xs">
            {transfer.sourceBranchName} → {transfer.destinationBranchName}
          </span>
        </span>
      ),
    },
    {
      key: 'quantity',
      header: t('inventory.fields.quantity'),
      align: 'end',
      render: (transfer) => (
        <span className="text-ink" dir="ltr">
          {transfer.totalReceivedQuantity !== null &&
          transfer.totalReceivedQuantity !== transfer.totalQuantity
            ? `${transfer.totalReceivedQuantity} / ${transfer.totalQuantity}`
            : transfer.totalQuantity}
        </span>
      ),
    },
    {
      key: 'lines',
      header: t('inventory.transfers.fields.lines'),
      align: 'end',
      render: (transfer) => (
        <span className="text-muted" dir="ltr">
          {transfer.lineCount}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('inventory.fields.state'),
      render: (transfer) => (
        <span className="inline-flex items-center gap-1.5">
          <TransferStatusBadge status={transfer.status} />
          {/* A difference only counts as a variance once nobody is waiting for the rest. */}
          {!transfer.isOpen && transfer.hasVariance ? (
            <span className="text-xs font-medium text-amber-700">
              {t('inventory.transfers.variance')}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'end',
      render: (transfer) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSelectedId(transfer.id)}
          aria-label={t('inventory.transfers.openNamed', { reference: transfer.reference })}
        >
          {t('inventory.transfers.open')}
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('inventory.transfers.title')}
        subtitle={t('inventory.transfers.subtitle')}
        actions={
          canTransfer && activeBranch ? (
            <Button onClick={() => setIsCreating(true)}>
              <Plus className="size-4" aria-hidden />
              {t('inventory.transfers.create')}
            </Button>
          ) : undefined
        }
      />

      {feedback ? <Alert tone="success">{feedback}</Alert> : null}

      {!activeBranch ? <Alert tone="warning">{t('inventory.transfers.selectBranch')}</Alert> : null}

      <Card>
        <CardHeader
          title={t('inventory.transfers.title')}
          subtitle={
            data ? t('inventory.transfers.count', { count: data.pagination.totalItems }) : undefined
          }
          action={
            <div className="flex flex-wrap items-center gap-2">
              <div className="w-40 sm:w-52">
                <Input
                  type="search"
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setPage(1);
                  }}
                  placeholder={t('inventory.transfers.searchPlaceholder')}
                  aria-label={t('inventory.transfers.searchLabel')}
                />
              </div>
              <div className="w-36">
                <Select
                  options={statusOptions}
                  value={status}
                  aria-label={t('inventory.fields.state')}
                  onChange={(event) => {
                    setStatus(event.target.value as StockTransferStatus | '');
                    setPage(1);
                  }}
                />
              </div>
            </div>
          }
        />

        <DataTable
          caption={t('inventory.transfers.tableCaption')}
          columns={columns}
          rows={data?.items}
          rowKey={(transfer) => transfer.id}
          isLoading={isPending}
          isError={isError}
          errorMessage={apiErrorMessage(error) ?? t('inventory.transfers.loadError')}
          onRetry={() => void refetch()}
          emptyIcon={ArrowLeftRight}
          emptyTitle={
            deferredSearch || status
              ? t('inventory.transfers.noMatches')
              : t('inventory.transfers.empty')
          }
          emptyDescription={
            deferredSearch || status
              ? t('inventory.transfers.noMatchesHint')
              : t('inventory.transfers.emptyHint')
          }
        />

        {data ? (
          <Pagination pagination={data.pagination} onPageChange={setPage} isBusy={isFetching} />
        ) : null}
      </Card>

      <CreateTransferDrawer
        isOpen={isCreating}
        onClose={() => setIsCreating(false)}
        onCreated={(reference) => setFeedback(t('inventory.transfers.created', { reference }))}
      />

      <TransferDetailDrawer
        transferId={selectedId}
        onClose={() => setSelectedId(null)}
        onDone={(message) => {
          setFeedback(message);
          setSelectedId(null);
        }}
      />
    </div>
  );
}
